import * as z from "zod/mini";
import { MAX_REPORT_NOTE_CHARS, REPORT_CATEGORIES } from "./safety.js";
import { BEAN_EXTRA } from "./room-token.js";

/**
 * Protocol v1 JSON messages (ADR 0011). Every message is an object with a `type`; the part before the
 * dot is its channel (`room.*`, `chat.*`, `match.*`, and `voice.*`: ADR 0008, P2-VOICE-01).
 * Connection-level messages (`hello`, `welcome`, `ping`, ...) have no prefix: channel `sys`.
 *
 * Client → server messages are untrusted: every field is bounded. Unknown fields are stripped and
 * unknown types rejected, so adding fields or types is backward compatible (see version.ts).
 */

/** Max length of an inbound text frame, checked BEFORE JSON.parse (P1-S12). */
export const MAX_CLIENT_MESSAGE_CHARS = 2048;
/**
 * Voice signals carry a WebRTC session description (2-3 KB for audio), so they may be longer: a frame
 * over MAX_CLIENT_MESSAGE_CHARS is read only if it starts with VOICE_SIGNAL_PREFIX and fits this.
 */
export const MAX_VOICE_SIGNAL_CHARS = 8192;
export const VOICE_SIGNAL_PREFIX = '{"type":"voice.signal"';
/** True if a raw inbound frame is within the size limits (checked before parsing it). */
export const frameFits = (raw: string) =>
  raw.length <= MAX_CLIENT_MESSAGE_CHARS ||
  (raw.length <= MAX_VOICE_SIGNAL_CHARS && raw.startsWith(VOICE_SIGNAL_PREFIX));
/** Max inputs in one `match.in` batch. Clients send at most one message per input tick. */
export const MAX_INPUT_BATCH = 8;
export const MAX_CHAT_CHARS = 200;
export const MAX_NAME_CHARS = 24;
/** Same limit as GameManifest players.max. */
export const MAX_PLAYERS = 8;
/** The highest bean level a room shows (ADR 0043: level 999 is about 50 million beans). */
export const MAX_LEVEL = 999;

const int = (min: number, max: number) => z.number().check(z.int(), z.gte(min), z.lte(max));

/**
 * Human text: at least one visible character, no control characters, and no bidi overrides
 * (U+202A-202E, U+2066-2069), which can make a name or message display as something else.
 */
const humanText = (max: number) =>
  z.string().check(z.maxLength(max), z.regex(/\S/), z.regex(/^[^\p{Cc}‪-‮⁦-⁩]*$/u));

/** Seat ids, message ids and other server-issued ids. */
const id = z.string().check(z.regex(/^[A-Za-z0-9_-]{1,40}$/));
const secret = z.string().check(z.regex(/^[A-Za-z0-9_-]{16,128}$/));
/** Same rule as GameManifest ids. */
const gameId = z.string().check(z.regex(/^[a-z][a-z0-9-]{0,39}$/));
/** A GameManifest loadout id (e.g. a tank role): same rule. */
const loadoutId = gameId;
const semver = z.string().check(z.maxLength(40), z.regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/));
const version = int(1, 65_535);
/** Input sequence number: positive, per player, increasing. */
const seq = int(1, 2 ** 31 - 1);
const slot = int(0, MAX_PLAYERS - 1);
const gameRef = z.object({ id: gameId, version: semver });

/**
 * What voice signalling relays between two peers (ADR 0008: browser peer-to-peer, signalled through
 * the room): a session description, or one ICE candidate. Bounded; the room doesn't read them.
 */
const voiceData = z.union([
  z.object({
    description: z.object({
      type: z.enum(["offer", "answer", "rollback"]),
      sdp: z.optional(z.string().check(z.maxLength(MAX_VOICE_SIGNAL_CHARS - 256))),
    }),
  }),
  z.object({
    candidate: z.object({
      candidate: z.string().check(z.maxLength(1024)),
      sdpMid: z.optional(z.nullable(z.string().check(z.maxLength(64)))),
      sdpMLineIndex: z.optional(z.nullable(int(0, 64))),
      usernameFragment: z.optional(z.nullable(z.string().check(z.maxLength(256)))),
    }),
  }),
]);
export type VoiceData = z.infer<typeof voiceData>;

// ─────────────────────────── client → server

/** Frozen part of hello: every version can read it (see acceptHello). */
export const helloHead = z.object({ type: z.literal("hello"), v: version });

export const clientMessage = z.discriminatedUnion("type", [
  /** First message on every connection. */
  z.object({
    type: z.literal("hello"),
    /** The client's PROTOCOL_VERSION. */
    v: version,
    /** Client build, for diagnostics (e.g. "web@1a2b3c4"). */
    build: z.optional(z.string().check(z.regex(/^[\w.@+-]{1,64}$/))),
    /** Reclaim a reserved seat after a disconnect (ADR 0004; P2-RT-06). */
    resume: z.optional(z.object({ seat: id, secret })),
  }),
  /** Keepalive. Send exactly PING; a Room DO answers PONG without waking from hibernation. */
  z.object({ type: z.literal("ping") }),
  /** Host: choose the game for the next match (also how players switch games). */
  z.object({ type: z.literal("room.select"), game: gameId }),
  /**
   * Host: start a match with the selected game (also rematch from the results screen). `mission`
   * picks one the room has reached (`room.state.reached`, P2-TANK-07); without it, the room's
   * current one.
   */
  z.object({
    type: z.literal("room.start"),
    mission: z.optional(gameId),
    /** Today's daily (P4-TANK-03): the room picks the day's mission and seed, open to all. */
    daily: z.optional(z.literal(true)),
  }),
  /**
   * Leave now and free the seat (a dropped connection keeps it reserved for 30 s instead). With
   * `to`, for a new room this player just made (#616): the others are offered its invite, so
   * friends move together. The secret is relayed to the room's members only, never stored or
   * logged.
   */
  z.object({
    type: z.literal("room.leave"),
    to: z.optional(z.object({ room: id, secret })),
  }),
  /** Your choice for the next match from the game's loadouts (e.g. a role); null for its default. */
  z.object({ type: z.literal("room.loadout"), loadout: z.nullable(loadoutId) }),
  /** You picked another extra for your bean (ADR 0018), so the room shows it now; null is plain. */
  z.object({
    type: z.literal("room.bean"),
    bean: z.nullable(z.string().check(z.regex(BEAN_EXTRA))),
  }),
  /**
   * Your level from your beans (ADR 0043, #585), so the room shows it on your bean. Said by the
   * page, like the bean: it's a look, never an advantage.
   */
  z.object({ type: z.literal("room.level"), level: int(1, MAX_LEVEL) }),
  /** Host: battles two against two (ADR 0028) or everyone for themselves. */
  z.object({ type: z.literal("room.teams"), on: z.boolean() }),
  /** Host: put a seat on a side (0 or 1) for two-against-two battles. */
  z.object({ type: z.literal("room.team"), seat: id, team: int(0, 1) }),
  /** The host removes a player (P2-SAFE-01): disconnected, and kept out of this room. */
  z.object({ type: z.literal("room.remove"), seat: id }),
  /** Report a player: a category and an optional note; the room adds its recent chat. */
  z.object({
    type: z.literal("room.report"),
    seat: id,
    category: z.enum(REPORT_CATEGORIES),
    note: z.optional(humanText(MAX_REPORT_NOTE_CHARS)),
  }),
  /** Block a player: no future rooms together where that can be prevented. */
  z.object({ type: z.literal("room.block"), seat: id }),
  z.object({ type: z.literal("chat.send"), text: humanText(MAX_CHAT_CHARS) }),
  /** The game bundle from `match.load` is loaded and its first frame can be drawn. */
  z.object({ type: z.literal("match.ready") }),
  /**
   * This page went to the background (`true`) or came back (`false`) during a match (P2-WEB-06).
   * Hidden for 3 s counts as away: the match pauses for everyone, as for a dropped connection.
   */
  z.object({ type: z.literal("match.away"), away: z.boolean() }),
  /** Voice (P2-VOICE-01): this page joined voice (`on`) or left it, and whether its mic is muted. */
  z.object({ type: z.literal("voice.state"), on: z.boolean(), muted: z.boolean() }),
  /** A WebRTC signal for one other seat in the room; the room relays it as is. */
  z.object({ type: z.literal("voice.signal"), to: id, data: voiceData }),
  /**
   * Inputs, oldest first, as [seq, input] pairs. `input` is the game's own Input JSON, validated by
   * the game's parseInput; the 2048-character frame cap bounds it.
   */
  z.object({
    type: z.literal("match.in"),
    b: z.array(z.tuple([seq, z.unknown()])).check(z.minLength(1), z.maxLength(MAX_INPUT_BATCH)),
  }),
]);

// ─────────────────────────── server → client

const range = { min: version, max: version };

export const serverMessage = z.discriminatedUnion("type", [
  /** Handshake accepted. Keep `seat` to resume after a disconnect. */
  z.object({
    type: z.literal("welcome"),
    /** Protocol version for this connection (the client's own). */
    v: version,
    seat: z.object({ id, secret }),
    /** True if the seat was reclaimed (`hello.resume`). */
    resumed: z.boolean(),
  }),
  /** Client protocol too old; followed by close 4001. Frozen. */
  z.object({ type: z.literal("reload"), ...range }),
  /** Client newer than the server (a deploy is rolling out); followed by close 4002. Frozen. */
  z.object({ type: z.literal("retry"), ...range, afterMs: int(0, 600_000) }),
  /** Keepalive answer. Exactly PONG. */
  z.object({ type: z.literal("pong") }),
  /** A request was refused. `about` names the message type it answers. */
  z.object({
    type: z.literal("error"),
    code: z.enum(["invalid", "forbidden", "bad_state", "rate_limited", "unavailable"]),
    about: z.optional(z.string().check(z.maxLength(40))),
  }),
  /** The whole room, sent on join and on every change (rooms are small; no incremental state). */
  z.object({
    type: z.literal("room.state"),
    members: z
      .array(
        z.object({
          seat: id,
          name: humanText(MAX_NAME_CHARS),
          connected: z.boolean(),
          /** Their choice for the next match (`room.loadout`), if they made one. */
          loadout: z.optional(loadoutId),
          /** Connected, but their page has been in the background for 3 s or more (`match.away`). */
          away: z.optional(z.boolean()),
          /** In voice, with the mic on or muted (`voice.state`); absent when not in voice. */
          voice: z.optional(z.enum(["on", "muted"])),
          /** Their seat's colour, an index into the seat colours (ADR 0018): kept while they're here. */
          colour: z.optional(int(0, 31)),
          /** The extra on their bean (ADR 0018), if they picked one. */
          bean: z.optional(z.string().check(z.regex(BEAN_EXTRA))),
          /** Their level from their beans (`room.level`, ADR 0043), once their page has said. */
          level: z.optional(int(1, MAX_LEVEL)),
        }),
      )
      .check(z.maxLength(MAX_PLAYERS)),
    host: id,
    game: z.nullable(gameRef),
    phase: z.enum(["lobby", "loading", "playing", "results"]),
    /** The game's mission the room plays next, or played last (P2-TANK-07). */
    mission: z.optional(gameId),
    /** The missions the room has reached, in order: a win opens the next one. */
    reached: z.optional(z.array(gameId).check(z.maxLength(32))),
    /** The host has the Obean Pass: nobody in the room sees ads (ADR 0026). */
    adFree: z.optional(z.boolean()),
    /** Two against two is on (ADR 0028): each seat's side, 0 or 1. Absent: everyone for themselves. */
    teams: z.optional(z.record(id, int(0, 1))),
    /**
     * While a match is played: the slots a bot took over from someone who left (ADR 0004), with
     * the name of the player it plays for. Absent when there are none.
     */
    bots: z.optional(
      z
        .array(z.object({ slot: int(0, MAX_PLAYERS - 1), for: humanText(MAX_NAME_CHARS) }))
        .check(z.maxLength(MAX_PLAYERS)),
    ),
  }),
  z.object({
    type: z.literal("chat.msg"),
    /** Server-assigned, so a message can be reported. */
    id,
    from: id,
    text: humanText(MAX_CHAT_CHARS),
    /** Server time (ms since epoch). */
    at: int(0, Number.MAX_SAFE_INTEGER),
  }),
  /** Load this exact game version (pinned per match, ADR 0004), then send `match.ready`. */
  z.object({
    type: z.literal("match.load"),
    /** The server's id for the match (since P2-WEB-03): what client analytics tag it with. */
    matchId: z.optional(id),
    game: gameRef,
    missionId: gameId,
    /** This client's slot. */
    slot,
    /** Who plays which slot, and in a two-against-two battle their side (ADR 0028). */
    players: z
      .array(z.object({ slot, seat: id, team: z.optional(int(0, 1)) }))
      .check(z.minLength(1), z.maxLength(MAX_PLAYERS)),
    /**
     * This match is traced (ADR 0035): the 1% sample, or a switch someone turned on. Each player's
     * device records how the match ran and uploads it at the end. Absent: not traced.
     */
    trace: z.optional(z.enum(["sample", "switch"])),
    /** Today's daily (P4-TANK-03): the UTC day whose mission and seed this is. */
    daily: z.optional(z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}$/))),
  }),
  /** A WebRTC signal from another seat (`voice.signal`, relayed). */
  z.object({ type: z.literal("voice.signal"), from: id, data: voiceData }),
  /**
   * Someone left for a new room and asks the others along (`room.leave` with `to`, #616): its id
   * and an invite secret, to join with. Sent once, just before the room.state without them.
   */
  z.object({
    type: z.literal("room.moved"),
    seat: id,
    name: humanText(MAX_NAME_CHARS),
    to: z.object({ room: id, secret }),
  }),
  /** The match is decided (the game's MatchOutcome). Snapshots stop; room.state moves to results. */
  z.object({
    type: z.literal("match.end"),
    result: z.enum(["win", "loss"]),
    stats: z.record(z.string().check(z.maxLength(40)), z.number()),
  }),
]);

export type ClientMessage = z.infer<typeof clientMessage>;
export type ServerMessage = z.infer<typeof serverMessage>;
/** One message type, e.g. `MessageOf<ClientMessage, "match.in">`. */
export type MessageOf<M extends { type: string }, T extends M["type"]> = Extract<M, { type: T }>;
export type Hello = MessageOf<ClientMessage, "hello">;

/** Keepalive frames, exactly as sent. A Room DO registers them with setWebSocketAutoResponse. */
export const PING = '{"type":"ping"}';
export const PONG = '{"type":"pong"}';

export const CHANNELS = ["sys", "room", "chat", "voice", "match"] as const;
export type Channel = (typeof CHANNELS)[number];

/** The channel a message belongs to, for routing and per-channel limits. */
export function channelOf(msg: ClientMessage | ServerMessage): Channel {
  const dot = msg.type.indexOf(".");
  return dot < 0 ? "sys" : (msg.type.slice(0, dot) as Channel);
}
